import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import type { ExactOnlineConnectionStatus, ExactOnlineDivision } from '@models/exact-online';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExactOnlineService } from '@services/exact-online.service';
import { ToastService } from '@services/toast.service';
import { firstValueFrom } from 'rxjs';

interface LocaleDefinition {
  code: string;
  label: string;
}

const LOCALES: LocaleDefinition[] = [
  { code: 'nl', label: 'NL' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'it', label: 'IT' },
  { code: 'fr', label: 'FR' },
];

@Component({
  selector: 'app-exact-online',
  templateUrl: './exact-online.component.html',
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExactOnlineComponent implements OnInit {
  private readonly exactOnlineService = inject(ExactOnlineService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly locales = LOCALES;
  readonly loading = signal(true);
  readonly connections = signal<Record<string, ExactOnlineConnectionStatus>>({});
  readonly divisions = signal<Record<string, ExactOnlineDivision[]>>({});
  readonly selectedDivision = signal<Record<string, string>>({});
  readonly busyLocales = signal<Set<string>>(new Set());
  readonly loadingDivisionsFor = signal<Set<string>>(new Set());
  readonly savingDivisionFor = signal<Set<string>>(new Set());

  async ngOnInit(): Promise<void> {
    this.handleOAuthReturn();
    await this.loadConnections();
  }

  private handleOAuthReturn(): void {
    const params = this.route.snapshot.queryParamMap;
    const result = params.get('result');
    if (!result) return;

    const locale = params.get('locale');
    if (result === 'success') {
      this.toastService.showSuccess(
        this.translateService.instant('exact-online.toast.connect-success', { locale: locale?.toUpperCase() }),
      );
    } else {
      this.toastService.showError(this.translateService.instant('exact-online.toast.connect-failed'));
    }

    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
  }

  private async loadConnections(): Promise<void> {
    this.loading.set(true);
    try {
      const list = await firstValueFrom(this.exactOnlineService.getConnections());
      const byLocale: Record<string, ExactOnlineConnectionStatus> = {};
      for (const connection of list) {
        byLocale[connection.locale] = connection;
      }
      this.connections.set(byLocale);

      for (const connection of list) {
        if (connection.status === 'Connected') {
          await this.loadDivisions(connection.locale, connection.division);
        }
      }
    } catch (error: unknown) {
      console.error('Failed to load Exact Online connections', error);
      this.toastService.showError(this.translateService.instant('exact-online.toast.load-failed'));
    } finally {
      this.loading.set(false);
    }
  }

  private async loadDivisions(locale: string, currentDivision: number | null): Promise<void> {
    this.setBusySet(this.loadingDivisionsFor, locale, true);
    try {
      const options = await firstValueFrom(this.exactOnlineService.getDivisions(locale));
      this.divisions.update((current) => ({ ...current, [locale]: options }));

      if (currentDivision !== null) {
        const match = options.find((option) => option.code === String(currentDivision));
        if (match) {
          this.selectedDivision.update((current) => ({ ...current, [locale]: match.code }));
        }
      }
    } catch (error: unknown) {
      console.error(`Failed to load Exact Online divisions for locale ${locale}`, error);
      this.toastService.showError(this.translateService.instant('exact-online.toast.divisions-failed'));
    } finally {
      this.setBusySet(this.loadingDivisionsFor, locale, false);
    }
  }

  async connect(locale: string): Promise<void> {
    this.setBusySet(this.busyLocales, locale, true);
    try {
      const { url } = await firstValueFrom(this.exactOnlineService.getAuthorizeUrl(locale));
      window.location.href = url;
    } catch (error: unknown) {
      console.error(`Failed to start Exact Online authorization for locale ${locale}`, error);
      this.toastService.showError(this.translateService.instant('exact-online.toast.connect-start-failed'));
      this.setBusySet(this.busyLocales, locale, false);
    }
  }

  async disconnect(locale: string): Promise<void> {
    this.setBusySet(this.busyLocales, locale, true);
    try {
      const updated = await firstValueFrom(this.exactOnlineService.disconnect(locale));
      this.connections.update((current) => ({ ...current, [locale]: updated }));
      this.divisions.update((current) => ({ ...current, [locale]: [] }));
      this.selectedDivision.update((current) => {
        const next = { ...current };
        delete next[locale];
        return next;
      });
      this.toastService.showSuccess(this.translateService.instant('exact-online.toast.disconnect-success'));
    } catch (error: unknown) {
      console.error(`Failed to disconnect Exact Online for locale ${locale}`, error);
      this.toastService.showError(this.translateService.instant('exact-online.toast.disconnect-failed'));
    } finally {
      this.setBusySet(this.busyLocales, locale, false);
    }
  }

  async saveDivision(locale: string): Promise<void> {
    const divisionCode = this.selectedDivision()[locale];
    if (!divisionCode) return;

    this.setBusySet(this.savingDivisionFor, locale, true);
    try {
      const updated = await firstValueFrom(this.exactOnlineService.setDivision(locale, divisionCode));
      this.connections.update((current) => ({ ...current, [locale]: updated }));
      this.toastService.showSuccess(this.translateService.instant('exact-online.toast.division-saved'));
    } catch (error: unknown) {
      console.error(`Failed to save Exact Online division for locale ${locale}`, error);
      this.toastService.showError(this.translateService.instant('exact-online.toast.division-save-failed'));
    } finally {
      this.setBusySet(this.savingDivisionFor, locale, false);
    }
  }

  onDivisionSelected(locale: string, code: string): void {
    this.selectedDivision.update((current) => ({ ...current, [locale]: code }));
  }

  getConnection(locale: string): ExactOnlineConnectionStatus | undefined {
    return this.connections()[locale];
  }

  getDivisionOptions(locale: string): ExactOnlineDivision[] {
    return this.divisions()[locale] ?? [];
  }

  isBusy(locale: string): boolean {
    return this.busyLocales().has(locale);
  }

  isLoadingDivisions(locale: string): boolean {
    return this.loadingDivisionsFor().has(locale);
  }

  isSavingDivision(locale: string): boolean {
    return this.savingDivisionFor().has(locale);
  }

  isAnyActionInProgress(): boolean {
    return this.busyLocales().size > 0 || this.savingDivisionFor().size > 0;
  }

  private setBusySet(source: typeof this.busyLocales, locale: string, value: boolean): void {
    source.update((current) => {
      const next = new Set(current);
      if (value) {
        next.add(locale);
      } else {
        next.delete(locale);
      }
      return next;
    });
  }
}
